import { Provider } from 'react-redux';
import { Suspense, lazy, useEffect, useState } from 'react';

import { main } from './configuration';

import { config } from './config';
import { createRoot } from 'react-dom/client';
import { store } from './configuration/setup/store';
import { handleLoginRedirect } from './configuration/login/redirect';
import type { User } from 'oidc-client-ts';
import { InvalidLogin } from './components/invalidLogin';
import { DEFAULT_LOCALE } from './configuration/lang/lang';
import { IntlProvider } from 'react-intl';
import { useDisplayMessages, useLocale } from './configuration/lang/langSlice';
import ErrorFallback from './components/ErrorFallback';
import { RouterProvider } from 'react-router';
import { router } from './routes/Router';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ConfigCatProvider, createConsoleLogger, DataGovernance, PollingMode } from 'configcat-react';
import {
    hasAnalyticsAnalysisOverlayParam,
    removeAnalyticsAnalysisOverlayParam,
} from './features/app/utils/analyticsAnalysisOverlayUtils';

const AnalyticsAnalysisOverlay = lazy(() =>
    import('@rio-cloud/rio-uikit/AnalyticsAnalysisOverlay').then(module => ({
        default: module.AnalyticsAnalysisOverlay,
    }))
);

const App = () => {
    const userLocale = useLocale();
    const displayMessages = useDisplayMessages();
    const [showAnalyticsAnalysisOverlay] = useState(() => {
        if (typeof window === 'undefined') {
            return false;
        }

        return hasAnalyticsAnalysisOverlayParam();
    });

    useEffect(() => {
        if (!showAnalyticsAnalysisOverlay) {
            return;
        }

        removeAnalyticsAnalysisOverlayParam();
    }, [showAnalyticsAnalysisOverlay]);

    if (!(displayMessages && userLocale)) {
        return null;
    }

    return (
        <IntlProvider defaultLocale={DEFAULT_LOCALE} key={userLocale} locale={userLocale} messages={displayMessages}>
            <ErrorBoundary fallback={<ErrorFallback />}>
                <ConfigCatProvider
                    sdkKey={config.configCat.apiKey}
                    pollingMode={PollingMode.AutoPoll}
                    options={{
                        pollIntervalSeconds: config.configCat.pollIntervalSeconds,
                        logger: createConsoleLogger(config.configCat.logLevel),
                        dataGovernance: DataGovernance.EuOnly,
                    }}
                >
                    <RouterProvider router={router} />
                    {showAnalyticsAnalysisOverlay && (
                        <Suspense fallback={null}>
                            <AnalyticsAnalysisOverlay
                                bannerStartCoordinates={{ top: Math.max(0, window.innerHeight - 85), left: 15 }}
                            />
                        </Suspense>
                    )}
                </ConfigCatProvider>
            </ErrorBoundary>
        </IntlProvider>
    );
};

const renderApplication = (user: User) => {
    const container = document.getElementById('root') as HTMLElement;
    const root = createRoot(container);

    let showApp = true;
    try {
        showApp = user ? user.profile.sub.startsWith('prod-rio-users:') : true;
    } catch (e) {
        console.error(e);
    }

    if (showApp) {
        // Note that we need to use the base "Router" with a "hash" history
        // because the "HashRouter" doesn't allow handing in a history
        // from the outside. So this is effectively a "HashRouter" despite
        // that not being obvious here
        root.render(
            <Provider store={store}>
                <App />
            </Provider>
        );
    } else {
        root.render(<InvalidLogin />);
    }
};

const isDev = import.meta.env.DEV;
const isProd = import.meta.env.PROD;
const isProdPreview = import.meta.env.VITE_PRODUCTION_PREVIEW;
const runsInIframe = 'parent' in window && window.parent !== window;

if ((isDev && config.enableMockServer) || isProdPreview) {
    import('../mocks/msw/browser').then(({ startWorker }) => startWorker()).then(() => main(renderApplication));
}

if (window.location.href.startsWith(config.login.redirectUri as string) && runsInIframe) {
    handleLoginRedirect();
} else if (isProd && !isProdPreview) {
    void main(renderApplication);
}
